import {
  ActionPattern,
  ForkEffect,
  call,
  put,
  race,
  take,
  takeEvery,
  takeLatest,
  takeLeading,
} from 'redux-saga/effects';

import { logError } from './log';
import { notifyError } from './notify';
import { publishStopLoading } from './publish';

type SagaWorkerType = (...args: any[]) => any;

interface CatchParamsType {
  withNotification?: boolean;
  errorMessage?: string | ((error: any) => string);
  stopLoadingOnCatch?: boolean;
  stopLoadingEvent?: (() => any) | null;
}

type TakeSafeType<
  P extends ActionPattern = ActionPattern,
  Fn extends SagaWorkerType = SagaWorkerType,
> = (
  pattern: P,
  worker: Fn,
  catchParams?: CatchParamsType,
) => ForkEffect<never>;

export function withCatch(
  saga: SagaWorkerType,
  {
    withNotification = false,
    errorMessage,
    stopLoadingOnCatch = false,
    stopLoadingEvent = null,
  }: CatchParamsType = {},
) {
  return function* withCatchSaga(...args) {
    let requestDone = false;
    let hasError = false;
    try {
      yield call(saga, ...args);
      requestDone = true;
    } catch (error: any) {
      hasError = true;
      logError(error);
      if (withNotification) {
        notifyError({
          message:
            (typeof errorMessage === 'function'
              ? errorMessage(error)
              : errorMessage) ||
            error.notificationMessage ||
            '',
          error,
        });
      }
    } finally {
      if (!requestDone || hasError) {
        if (stopLoadingOnCatch) {
          yield call(publishStopLoading);
        }
        if (stopLoadingEvent) {
          yield put(stopLoadingEvent());
        }
      }
    }
  };
}

export function withCancel(saga: SagaWorkerType, action: string) {
  return function* _cancel(...args: Parameters<SagaWorkerType>) {
    yield put({ type: action });
    yield race({
      task: call(saga, ...args),
      cancel: take(action),
    });
  };
}

export const withNotificationError = (errorMessage: string) => ({
  withNotification: true,
  errorMessage,
});

export const takeEverySafe: TakeSafeType = (action, saga, catchParams) =>
  takeEvery(action, withCatch(saga, catchParams));

export const takeLatestSafe: TakeSafeType = (action, saga, catchParams) =>
  takeLatest(action, withCatch(saga, catchParams));

export const takeFirst = takeLeading;

export const takeFirstSafe: TakeSafeType = (action, saga, catchParams) =>
  takeFirst(action, withCatch(saga, catchParams));
